<template>
  <LoaderContent ref="loader">
    <div class="deals__wrapper">
      <div class="main-list all-disputes__list" v-if="deals.items.length === 0">
        <div class="main-item all-deals__item deal deal__not_found">
          {{$t('labels.deals_not_found')}}
        </div>
      </div>

      <div class="deals__container" v-else>
        <div style="overflow: auto">
          <table class="table table__flex" style="position: relative">
            <thead v-if="!hideHeader">
            <tr>
              <th class="text-start p"><span class="text-capitalize-first">{{ $t('labels.id_date')}}</span></th>
              <th class="text-start p"><span class="text-capitalize-first">{{ $t('labels.status')}}</span></th>
              <th class="text-start p"><span class="text-capitalize-first">{{ rateHeader }}</span></th>
              <th class="text-start p"><span class="text-capitalize-first">{{ $t('labels.sum')}}</span></th>
              <th class="text-start p" style="width: 0"><span class="text-capitalize-first">{{ $t('labels.device')}}</span></th>
              <th class="text-start p"><span class="text-capitalize-first">{{ $t('labels.requisites')}}</span></th>
              <th class="text-start p"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="deal in deals.items">
              <td class="pt pb ps flex-1">
                <div class="d-flex align-items-center gap-sm">
                  <div class="">
                    <SvgIcon v-if="[DealStatus.NEW, DealStatus.TRANSFER_CONFIRMED, DealStatus.TRANSFER_WAITING].includes(deal.status)" class="main-icon payment__icon" :icon="`deal-${deal.type}`"></SvgIcon>
                    <SvgIcon v-else-if="deal.status === DealStatus.COMPLETED" class="main-icon payment__icon payment__icon__completed" icon="deal-completed"></SvgIcon>
                    <SvgIcon v-else-if="deal.status === DealStatus.DISPUTE" class="main-icon payment__icon payment__icon__dispute" icon="deal-dispute"></SvgIcon>
                    <SvgIcon v-else-if="deal.status === DealStatus.CANCELED" class="main-icon payment__icon payment__icon__canceled" icon="deal-canceled"></SvgIcon>
                  </div>
                  <div class="d-flex flex-column">
                    <div>
                      <span class="badge" :class="{'badge__warning': deal.type === Direction.PAY_OUT, 'badge__brand': deal.type === Direction.PAY_IN}">{{ $t(`direction.${deal.type}`) }}</span>
                    </div>

                    <div class="d-flex align-items-center gap-xs">
                      <span class="reception__number">{{deal.publicId}}</span> <copy-button :value="deal.publicId"></copy-button>
                    </div>
                    <div><small class="text-secondary">{{ dateHelper.format(deal.createdAt) }}</small></div>
                  </div>
                </div>
              </td>
              <td class="pt pb flex-1 justify-content-end">
                <div class="d-flex flex-column  sm-align-items-end">
                  <div class="payment__info payment__info--status">
                    <div class="payment__status">
                      <span class="text-capitalize-first status-text no-wrap">{{ $t(`deal_status.${deal.status}`) }}</span>
                      <SvgIcon class="icon icon__warning icon__small d-flex" v-if="deal.isDisputeAutocompletingSuspend" icon="pause"></SvgIcon>
                      <PaymentTime v-if="[DealStatus.NEW, DealStatus.TRANSFER_CONFIRMED, DealStatus.TRANSFER_WAITING].includes(deal.status)" :value="getFormattedCancelTime(deal)"></PaymentTime>
                      <PaymentTime v-else-if="deal.disputeAutocloseAt && DealStatus.DISPUTE === deal.status" :value="getFormattedDisputeAutocloseTime(deal)"></PaymentTime>
                      <a v-if="deal.disputeAttachment" :href="`${api.baseUrl()}/attachments/${deal.disputeAttachment}`" target="_blank">
                        <SvgIcon class="icon icon__warning icon__small" icon="paperclip"></SvgIcon>
                      </a>
                      <template v-for="attachment in deal.attachments" :key="attachment.id">
                        <a :href="`${api.baseUrl()}/attachments/${attachment.file}`" target="_blank">
                          <SvgIcon class="icon icon__warning icon__small" icon="paperclip"></SvgIcon>
                        </a>
                        <ReceiptCheckControl :deal="deal" :attachment="attachment"></ReceiptCheckControl>
                      </template>
                    </div>
                    <small v-if="deal.disputeReason === DisputeReason.INVALID_SUM || deal.disputeReason === DisputeReason.PART_PAYOUT" class="text-danger">
                      (<span class="text-lowercase">{{ $t(`dispute_reason.${deal.disputeReason}`) }} </span>: {{numberFormatter.formatNumber(deal.disputeReasonData.amount, 2)}} {{deal.takerSum.currency}})
                    </small>
                    <ReceiptCheckStatus v-else-if="isPayOutReceiptCheck(deal.type, deal.disputeReason)" :reason="deal.disputeReason" :data="deal.disputeReasonData"></ReceiptCheckStatus>
                    <small v-else-if="deal.disputeReason" class="text-lowercase text-danger">
                      ({{ $t(`dispute_reason.${deal.disputeReason}`)}})
                    </small>
                    <div v-if="deal.transactionId" class="mt-1">
                      <small class="text-secondary">{{ $t('labels.transaction') }}: <span v-b-tooltip.hover :title="deal.transactionId">{{ deal.transactionId.length > 8 ? deal.transactionId.substring(0, 4) + '...' + deal.transactionId.substring(deal.transactionId.length - 4) : deal.transactionId }}</span> <copy-button :value="deal.transactionId"></copy-button></small>
                    </div>
                  </div>
                  <div>
                    <small class="text-secondary">{{ dateHelper.format(deal.updatedAt) }}</small>
                  </div>
                </div>
              </td>
              <td class="pt pb">
                <div v-if="(isTrader || isTraderOperator) && store.getters.getAccountCurrencies.includes(deal.takerSum.currency)"></div>
                <div v-else class="reception__rate rate-info">
                  <div class="rate-info__mobile-label">
                    <span class="text-capitalize-first">{{ rateHeader }}</span>
                  </div>
                  <span>{{ numberFormatter.formatNumber(deal.takerSum.amount / deal.makerSum.amount, deal.takerSum.subunit) }} {{ deal.takerSum.currency }}</span>
                  <span v-if="deal.merchantMakerSum"> / {{ numberFormatter.formatNumber(deal.takerSum.amount / deal.merchantMakerSum.amount, deal.takerSum.subunit) }} {{ deal.takerSum.currency }}</span>
                  <template v-if="isEffectiveRateEnabled && deal.rewardSum">
                    <span> / {{ numberFormatter.formatNumber((deal.takerSum.amount / deal.makerSum.amount) * (1 + deal.rewardSum?.amount / deal.makerSum.amount), deal.takerSum.subunit) }} {{ deal.takerSum.currency }}</span>
                  </template>
                </div>
              </td>
              <td class="pt pb">
                <div class="rate-info__mobile-label">
                  <span class="text-capitalize-first">{{$t('labels.sum')}}</span>
                </div>
                <div class="reception__sums">
                  <div class="reception__sum-rub sum-info__rub" v-if="deal.actualTakerSum">
                    <div class="d-flex align-items-center gap-xs no-wrap">
                      {{ numberFormatter.formatMoney(deal.actualTakerSum) }}
                      <copy-button :value="deal.actualTakerSum.amount"></copy-button>
                    </div>
                  </div>
                  <div class="reception__sum-rub sum-info__rub" v-if="!deal.actualTakerSum || deal.isActualSumsChanged" :class="{'text-decoration-line-through': deal.isActualSumsChanged}">
                    <div class="d-flex align-items-center gap-xs no-wrap">
                      {{ numberFormatter.formatMoney(deal.takerSum) }}
                      <copy-button :value="deal.takerSum.amount"></copy-button>
                    </div>
                  </div>
                  <div v-if="(isTrader || isTraderOperator) && store.getters.getAccountCurrencies.includes(deal.takerSum.currency)"></div>
                  <div class="reception__sum-currency sum-info__currency" v-else-if="deal.actualMakerSum">
                    <div class="d-flex align-items-center gap-xs no-wrap">
                      {{ numberFormatter.formatMoney(deal.actualMakerSum) }}
                      <copy-button :value="deal.actualMakerSum.amount"></copy-button>
                    </div>
                  </div>
                  <div v-if="(isTrader || isTraderOperator) && store.getters.getAccountCurrencies.includes(deal.takerSum.currency)"></div>
                  <div class="reception__sum-currency sum-info__currency" v-else-if="!deal.actualMakerSum || deal.isActualSumsChanged" :class="{'text-decoration-line-through': deal.isActualSumsChanged}">
                    <div class="d-flex align-items-center gap-xs no-wrap">
                      {{ numberFormatter.formatMoney(deal.makerSum) }}
                      <copy-button :value="deal.makerSum.amount"></copy-button>
                    </div>
                  </div>
                  <div v-if="(isTrader || isTraderOperator) && store.getters.getAccountCurrencies.includes(deal.takerSum.currency)"></div>
                  <div v-else-if="deal.minTakerSum && deal.maxTakerSum" class="reception__sum-rub sum-info__rub mt-1">
                    <small v-if="deal.minTakerSum?.amount !== deal.maxTakerSum?.amount" class="text-secondary">{{ numberFormatter.formatMoney(deal.minTakerSum) }} - {{ numberFormatter.formatMoney(deal.maxTakerSum) }}</small>
                    <small v-else class="text-secondary">{{ numberFormatter.formatMoney(deal.minTakerSum) }}</small>
                  </div>
                  <div v-else-if="(isTrader || isTraderOperator) && store.getters.getAccountCurrencies.includes(deal.takerSum.currency)"></div>
                  <div v-if="deal.minInTakerSum && deal.maxInTakerSum" class="reception__sum-currency sum-info__currency mt-1">
                    <small v-if="deal.minInTakerSum?.amount !== deal.maxInTakerSum?.amount" class="text-secondary">{{ numberFormatter.formatMoney(deal.minInTakerSum) }} - {{ numberFormatter.formatMoney(deal.maxInTakerSum) }}</small>
                    <small v-else class="text-secondary">{{ numberFormatter.formatMoney(deal.minInTakerSum) }}</small>
                  </div>
                  <div class="payment__restriction" v-if="deal.strictlySingleTransfer === true">
                    <svg-icon class="icon icon__warning icon__small" icon="warning"></svg-icon> {{ $t('labels.strictly_single_transfer')}}
                  </div>
                  <div class="small text-secondary" v-if="deal.payOutReceiptsHint">
                    {{ $t('labels.pay_out_receipts_hint_card') }}: {{ deal.payOutReceiptsHint }}
                  </div>
                </div>
              </td>
              <td class="pt pb">
                <div class="reception__sum sum-info" v-if="deal.type === Direction.PAY_IN">
                  <div class="rate-info__mobile-label">
                    <span class="text-capitalize-first">{{$t('labels.device')}}</span>
                  </div>
                  <span v-if="deal._device" style="display:flex;">
                    <img :src="require(`@/apps/public/assets/img/device-${deal._device.status}.svg`)">
                    {{ deal._device?.name }}
                  </span>
                </div>

              </td>
              <td class="pt pb">
                <div class="reception__card card-info">
                  <div class="d-flex align-items-center gap-sm">
                    <div class="">
                      <PayMethodIcon icon-class="pay-method-icon-xl" :method="deal.paymentMethod"></PayMethodIcon>
                    </div>
                    <div>
                      <div class="card-info__title">
                    <span>{{ $t(`payment_options.${deal.paymentOption}`) }}
                    <span v-if="deal.requisites?.crossBorderCurrency">{{deal.requisites?.crossBorderCurrency}}</span> <span v-if="deal.requisites?.paymentSystem">{{ $t('payment_systems.' + deal.requisites?.paymentSystem) }}</span>
                  </span>
                      </div>
                      <div class="card-info__number">
                    <span v-if="deal.type === Direction.PAY_OUT && deal.status === DealStatus.NEW">
                      <small class="text-secondary text-wrap">{{ $t('labels.accept_deal_to_show_req')}}</small>
                    </span>
                        <PayoutQrBlock
                          v-else-if="deal.type === Direction.PAY_OUT && deal.payoutQr?.link"
                          :link="deal.payoutQr.link"
                          :compact="true"
                        />
                        <VietQrPaymentBlock
                          v-else-if="helper.isEmvQrOption(deal.paymentOption)"
                          :deal-id="deal.id"
                          :hide-qr="true"
                          :qr-code-data="deal.qrCodeData ?? null"
                          :qr-code-link="helper.emvQrData(deal)"
                          :fallback-requisites="deal.paymentRequisites.requisites || ''"
                        />
                        <VietQrPaymentBlock
                          v-else-if="deal.paymentOption === PaymentOption.MANUAL_SBP_QR && helper.dealQrCodeLink(deal)"
                          :deal-id="deal.id"
                          :hide-qr="true"
                          :qr-code-data="null"
                          :qr-code-link="helper.dealQrCodeLink(deal)"
                          :fallback-requisites="''"
                        />
                        <small v-else-if="deal.paymentOption === PaymentOption.MANUAL_SBP_QR" class="text-secondary text-wrap">{{ $t('labels.manual_sbp_qr_awaiting_link') }}</small>
                        <a v-else-if="deal.paymentOption === PaymentOption.SBP_QR && deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.domain(deal.paymentRequisites.requisites) }}</a>
                        <a v-else-if="deal.paymentOption === PaymentOption.CLICK_UZ_QR && deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ $t('labels.my_click_uz') }}</a>
                        <a v-else-if="deal.paymentOption === PaymentOption.TIPS && deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.domain(deal.paymentRequisites.requisites) }}</a>
                        <div v-else-if="deal.paymentRequisites.requisites?.startsWith('http')" class="d-flex align-items-center gap-xs">
                          <a :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.domain(deal.paymentRequisites.requisites) }}</a> <copy-button :value="deal.paymentRequisites.requisites"></copy-button>
                        </div>
                        <div v-else-if="deal.paymentOption === PaymentOption.MANUAL_ECOM || (deal.type === Direction.PAY_OUT && deal.paymentOption === PaymentOption.TO_CARD)" class="d-flex align-items-center gap-xs">
                          {{ deal.paymentRequisites.requisites.replace(/(\d{4})/g, '$1 ').trim() }} <copy-button :value="deal.paymentRequisites.requisites.replace(/(\d{4})/g, '$1 ').trim()"></copy-button>
                        </div>
                        <div v-else class="d-flex align-items-center gap-xs">
                          {{ deal.paymentRequisites.requisites }} <copy-button :value="deal.paymentRequisites.requisites"></copy-button>
                        </div>
                      </div>
                      <div class="card-info__name">
                        <div v-if="deal.paymentOption === PaymentOption.SBP_QR && deal.requisites && !deal.requisites.requisites.startsWith('http')">
                          {{ deal.requisites.requisites }}
                        </div>
                        <div>
                          {{ deal.paymentRequisites.holder }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
              <td class="pt pb pe">
                <div class="payment__controls"  v-if="deal.type === Direction.PAY_OUT ">
                  <button @click.prevent="showDealDetails(deal)" type="button" @click="" class="btn">
                    <svg-icon icon="details"></svg-icon>
                  </button>
                  <button @click.prevent="refuseDeal(deal)" class="btn btn--reset" v-if="deal.status === DealStatus.TRANSFER_WAITING || deal.status === DealStatus.DISPUTE">
                    <svg-icon icon="cancel"></svg-icon>
                  </button>
                  <button @click.prevent="showPayOutDealSla(deal)" v-if="hasPayOutDealSla(deal)" type="button" class="btn no-wrap">
                    <span class="text-capitalize-first">{{ $t('button.sla') }}</span>
                  </button>
                  <button @click.prevent="acceptDeal(deal)" class="btn btn--accept no-wrap" v-if="deal.status === DealStatus.NEW && !isExpired(deal)">
                    <svg-icon icon="clock"></svg-icon>
                    {{ getFormattedCancelTime(deal) }} <span class="text-capitalize-first">{{ $t('button.accept') }}</span>
                  </button>
                  <button @click.prevent="confirmPayoutDeal(deal)" v-if="user.isPayoutConfirmationFromWebAppAllowed && (deal.status === DealStatus.TRANSFER_WAITING || deal.status === DealStatus.DISPUTE)" type="button" class="btn btn--accept">
                    <svg-icon icon="check-small"></svg-icon>
                  </button>
                  <button @click.prevent="requestVideo(deal)" v-if="deal.isVideoRequestAvailable" :disabled="isVideoRequested(deal)" :title="isVideoRequested(deal) ? dateHelper.format(deal.videoRequestedUntil) : null" type="button" class="btn no-wrap">
                    <span class="text-capitalize-first">{{ isVideoRequested(deal) ? $t('button.video_requested') : $t('button.request_video') }}</span>
                  </button>

                </div>
                <div class="payment__controls"  v-if="deal.type === Direction.PAY_IN ">
                  <button @click.prevent="showDealDetails(deal)" type="button" class="btn">
                    <svg-icon icon="details"></svg-icon>
                  </button>
                  <button @click.prevent="confirmDeal(deal)" v-if="[DealStatus.DISPUTE, DealStatus.TRANSFER_CONFIRMED, DealStatus.TRANSFER_WAITING, DealStatus.CANCELED].includes(deal.status)" type="button" class="btn btn--accept">
                    <svg-icon icon="check-small"></svg-icon>
                  </button>
                  <button @click.prevent="requestVideo(deal)" v-if="deal.isVideoRequestAvailable" :disabled="isVideoRequested(deal)" :title="isVideoRequested(deal) ? dateHelper.format(deal.videoRequestedUntil) : null" type="button" class="btn no-wrap">
                    <span class="text-capitalize-first">{{ isVideoRequested(deal) ? $t('button.video_requested') : $t('button.request_video') }}</span>
                  </button>

                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <b-pagination
          v-if="deals.totalCount > deals.items.length"
          v-model="page"
          :total-rows="deals.totalCount"
          :per-page="deals.limit"
          :limit="20"
          @update:modelValue="load()"
          :prev-text="$t('button.back')"
          :next-text="$t('button.next')"
          align="start"
          first-number
          hide-goto-end-buttons
          hide-ellipsis
        ></b-pagination>
      </div>
    </div>
  </LoaderContent>
  <DealDetailsPopup ref="dealDetailsPopup"></DealDetailsPopup>
  <CancelReasonPopup ref="cancelReasonPopup"></CancelReasonPopup>
  <ConfirmPopup ref="confirmPopup"></ConfirmPopup>
  <ConfirmPayoutPopup ref="confirmPayoutPopup"></ConfirmPayoutPopup>
  <PayOutDealSlaPopup ref="payOutDealSlaPopup"></PayOutDealSlaPopup>
</template>
<script lang="ts">
import {computed, defineComponent, onMounted, onUnmounted, PropType, reactive, ref, toRefs} from 'vue'
import {$t, helper} from "@/service/CommonHelper";
import {useRoute} from "vue-router";
import {useStore} from "vuex";
import {Direction} from "@/models/enum/Direction";
import DealRequestDTO, {defaultRequestDTO} from "@/models/request/DealRequestDTO";
import {DealStatus} from "@/models/enum/DealStatus";
import LoaderContent from "@/apps/public/components/LoaderContent.vue";
import PaginatedResponseDTO from '@/models/PaginatedResponseDTO';
import ExtendedDealDTO from '@/models/ExtendedDealDTO';
import OrderByDTO from "@/models/OrderByDTO";
import {dateHelper} from "@/service/DateHelper";
import {api} from '@/service/APIClient';
import {Dictionary} from "@/types";
import TraderDeviceDTO from '@/models/TraderDeviceDTO';
import PaginationDTO from "@/models/PaginationDTO";
import {BPagination} from "bootstrap-vue-3";
import SvgIcon from "@/apps/public/components/SvgIcon.vue";
import ReceiptCheckControl from "@/apps/public/components/ReceiptCheckControl.vue";
import CopyButton from "@/apps/public/components/CopyButton.vue";
import {DisputeReason, isPayOutReceiptCheck} from "@/models/enum/DisputeReason";
import ReceiptCheckStatus from "@/components/ReceiptCheckStatus.vue";
import {numberFormatter} from "@/service/NumberFormatter";
import {formatMinutesSeconds} from "@/service/TimeFormatter";
import TraderDealDTO from "@/models/TraderDealDTO";
import moment from "moment";
import PaymentTime from "@/apps/public/components/PaymentTime.vue";
import PayMethodIcon from "@/components/PayMethodIcon.vue";
import {PaymentOption} from "@/models/enum/PaymentOption";
import DealDetailsPopup from "@/apps/public/components/DealDetailsPopup.vue";
import ConfirmPopup from "@/apps/public/components/ConfirmPopup.vue";
import {useNotification} from '@kyvg/vue3-notification';
import CancelReasonPopup from "@/apps/public/components/CancelReasonPopup.vue";
import ConfirmPayoutPopup from "@/apps/public/components/ConfirmPayoutPopup.vue";
import LoaderButton from "@/apps/public/components/LoaderButton.vue";
import Multiselect from "vue-multiselect";
import {DatePicker} from "v-calendar";
import DevicesMultiselect from "@/components/DevicesMultiselect.vue";
import VietQrPaymentBlock from "@/components/VietQrPaymentBlock.vue";
import PayoutQrBlock from "@/apps/public/components/PayoutQrBlock.vue";
import {payoutAcceptHint} from "@/service/PayoutLimitHelper";
import TraderPayoutLimitDTO from "@/models/TraderPayoutLimitDTO";
import PayOutDealSlaPopup from "@/apps/public/components/PayOutDealSlaPopup.vue";

export default defineComponent({
  props: {
    hideHeader: {
      type: Boolean,
      default: false,
    },
    payoutLimit: {
      type: Object as PropType<TraderPayoutLimitDTO | null>,
      default: null,
    },
  },
  emits: ['payoutLimitChanged'],
  methods: {$t},
  components: {
    ReceiptCheckStatus,
    VietQrPaymentBlock,
    PayoutQrBlock,
    DevicesMultiselect,
    DatePicker,
    LoaderButton,
    ConfirmPayoutPopup,
    PayOutDealSlaPopup,
    CancelReasonPopup,
    ConfirmPopup,
    DealDetailsPopup,
    PayMethodIcon,
    PaymentTime,
    CopyButton,
    SvgIcon,
    ReceiptCheckControl,
    BPagination,
    LoaderContent,
    Multiselect
  },
  setup(props, {emit}) {
    const notification = useNotification()
    const loader = ref<InstanceType<typeof LoaderContent> | null>(null)
    const dealDetailsPopup = ref<InstanceType<typeof DealDetailsPopup> | null>(null)
    const confirmPopup = ref<InstanceType<typeof ConfirmPopup> | null>(null)
    const cancelReasonPopup = ref<InstanceType<typeof CancelReasonPopup> | null>(null)
    const confirmPayoutPopup = ref<InstanceType<typeof ConfirmPayoutPopup> | null>(null)
    const payOutDealSlaPopup = ref<InstanceType<typeof PayOutDealSlaPopup> | null>(null)
    const store = useStore()
    const route = useRoute()
    const isEffectiveRateEnabled = computed(() => (store.getters.getUser?.isEffectiveRateEnabled ?? false) && (store.getters.isTrader || store.getters.isTraderOperator))
    const state = reactive({
      devicesFilter: [] as TraderDeviceDTO[],
      currentTimestamp: Date.now(),
      orderByDTO: {
        field: 'createdAt',
        direction: 'desc',
      } as OrderByDTO,
      requestDTO: {
        ...defaultRequestDTO
      } as DealRequestDTO,
      deals: {
        limit: 20,
        offset: 0,
        totalCount: 0,
        items: []
      } as PaginatedResponseDTO<ExtendedDealDTO>,
      page: 1,
      payOutDocLink: '' as string,
      payOutSupportedMethodsSources: [] as string[],
      hidePayoutSupportedMethods: process.env.VUE_APP_HIDE_PAYOUT_SUPPORTED_METHODS === undefined ? 0 : Number(process.env.VUE_APP_HIDE_PAYOUT_SUPPORTED_METHODS),
      updateCurrentTimeInterval: null as any,
    });

    const rateHeader = computed(() => {
      if ((store.getters.isAdmin || store.getters.isOperator) && store.getters.getAppData?.DEALS_STORE_RATE_ENABLED) {
        return $t('labels.trader_rate_merchant_rate') as string
      }
      const base = $t('labels.rate') as string
      if (!isEffectiveRateEnabled.value) {
        return base
      }
      return `${base} / ${$t('labels.effective_rate_short')}`
    })


    const update = async (requestDTO: DealRequestDTO, page: number, devicesFilter: TraderDeviceDTO[]) => {
      state.devicesFilter = devicesFilter
      state.requestDTO = requestDTO
      state.page = page
      load()
    }

    const load = () => {
      loader.value?.load(_load)
    }

    const _load = async () => {
      state.deals.offset = state.deals.limit * (state.page - 1)
      state.requestDTO.deviceIds = state.devicesFilter.map((value) => value.id)

      const deals = await api.deals(state.deals, state.orderByDTO, {
        ...state.requestDTO,
        fromDate: dateHelper.toUTC(state.requestDTO.fromDate),
        toDate: dateHelper.toUTC(state.requestDTO.toDate),
        updatedFromDate: dateHelper.toUTC(state.requestDTO.updatedFromDate),
      })

      if (store.getters.isAdmin || store.getters.isOperator || store.getters.isTrader || store.getters.isTraderOperator) {
        let deviceIds = [];
        for (const item of deals.items) {
          if (item.deviceId) {
            deviceIds.push(item.deviceId)
          }
        }

        let devices = {} as Dictionary<TraderDeviceDTO>;
        if (deviceIds.length > 0) {
          for (const device of (await api.traderDevices({offset:0, limit: deviceIds.length} as PaginationDTO, {ids: deviceIds})).items) {
            devices[device.id.toString()] = device
          }
        }

        for (const item of deals.items) {
          if (item.deviceId) {
            item._device = devices[item.deviceId.toString()] ?? null;
          }
        }
      }

      state.deals = deals
    }

    const payOutSupportedMethodsSources = async () => {
      state.payOutDocLink = await api.payOutDocLink();
      if (!state.payOutDocLink) {
        state.payOutSupportedMethodsSources = await api.payOutSupportedMethodsSources();
      }
    }

    const getFormattedCancelTime = (deal: TraderDealDTO): string => {
      const canceledAt = moment.utc(deal.canceledAt);

      let seconds = canceledAt.unix() * 1000 - state.currentTimestamp;
      if (seconds < 0) {
        return '00:00';
      }

      return formatMinutesSeconds(seconds);
    }

    const refuseDeal = async (deal: TraderDealDTO) => {
      cancelReasonPopup.value?.show(deal, () => {
        load()
      })
    }

    const confirmPayoutDeal = async (deal: TraderDealDTO) => {
      confirmPayoutPopup.value?.show(deal, () => {
        load()
      })
    }

    const isVideoRequested = (deal: TraderDealDTO): boolean => {
      return deal.videoRequestedUntil != null && new Date(deal.videoRequestedUntil) > new Date()
    }

    const requestVideo = async (deal: TraderDealDTO) => {
      confirmPopup.value?.show({
        title: $t('labels.deal_action_confirmation', {action: $t('button.request_video')}),
        okTitle: $t('button.request_video'),
        onOkClick: async () => {
          try {
            await api.call.traderDeals.requestVideo(deal.id);
            notification.notify({type: 'success', title: $t('success.video_request_sent')});
            load()
          } catch (e) {
            if (helper.isValidationErrors(e)) {
              for (const item of helper.getValidationErrors(e)) {
                notification.notify({type: 'error', title: $t(`server_errors.${item.code}`, {}, item.message)});
              }
              load()
            }
          }
        }
      })
    }

    const acceptDeal = async (deal: TraderDealDTO) => {
      showDealConfirmation(deal, 'accept')
    }

    const confirmDeal = async (deal: TraderDealDTO) => {
      showDealConfirmation(deal, 'confirm')
    }

    const showDealConfirmation = (deal: TraderDealDTO, action: string) => {
      const requisite = action === 'confirm' ? (deal.paymentRequisites?.requisites ?? '').trim() : '';
      let confirmTitle = requisite !== ''
        ? $t('labels.deal_action_confirmation_sum_requisite', {action: $t(`deals.action.${action}`), sum: numberFormatter.formatMoney(deal.takerSum), requisite}) as string
        : $t('labels.deal_action_confirmation_sum', {action: $t(`deals.action.${action}`), sum: numberFormatter.formatMoney(deal.takerSum)}) as string;
      if (action === 'accept' && deal.type === Direction.PAY_OUT) {
        const hint = payoutAcceptHint(props.payoutLimit, deal.takerSum.currency);
        if (hint) {
          confirmTitle += '. ' + $t('labels.payin_balance_available_for_accept', hint);
        }
      }

      confirmPopup.value?.show({
        title: confirmTitle,
        okTitle: $t(`deals.action.${action}`),
        onOkClick: async () => {
          try {
            await api.dealChangeStatus(deal.id, action);
            if (deal.type === Direction.PAY_OUT) {
              emit('payoutLimitChanged')
            }
            load()
          } catch (e) {
            if (helper.isValidationErrors(e)) {
              for (const item of helper.getValidationErrors(e)) {
                notification.notify({type: 'error', title: $t(`server_errors.${item.code}`, {}, item.message)});
              }
            }
          }
        }
      })
    }

    const getFormattedDisputeAutocloseTime = (deal: TraderDealDTO): string => {
      if (!deal.disputeAutocloseAt) {
        return '';
      }

      const disputeAutocloseAt = moment.utc(deal.disputeAutocloseAt);

      let seconds = disputeAutocloseAt.unix() * 1000 - state.currentTimestamp;
      if (seconds < 0) {
        return '00:00';
      }

      return formatMinutesSeconds(seconds);
    }

    const updateCurrentTime = () => {
      state.currentTimestamp = Date.now()
    }

    const showDealDetails = async (deal: TraderDealDTO) => {
      dealDetailsPopup.value?.show(deal)
    }

    const isExpired = (deal: TraderDealDTO) => {
      if (deal.type === Direction.PAY_OUT && deal.status === DealStatus.NEW) {
        const canceledAt = moment.utc(deal.canceledAt);

        let seconds = canceledAt.unix() * 1000 - state.currentTimestamp;
        if (seconds < 0) {
          return true;
        }
      }

      return false;
    }

    const hasPayOutDealSla = (deal: TraderDealDTO): boolean => {
      return deal.status === DealStatus.NEW && !isExpired(deal) && (deal.payOutDealSla ?? '').trim() !== ''
    }

    const showPayOutDealSla = (deal: TraderDealDTO) => {
      payOutDealSlaPopup.value?.show((deal.payOutDealSla ?? '').trim());
    }

    onMounted(async () => {
      payOutSupportedMethodsSources()
      updateCurrentTime()
      state.updateCurrentTimeInterval = setInterval(updateCurrentTime, 1000)
    })

    onUnmounted(() => {
      if (state.updateCurrentTimeInterval) {
        clearInterval(state.updateCurrentTimeInterval)
      }
    })


    return {
      ...toRefs(state),
      user: computed(() => store.getters.getUser),
      getFormattedDisputeAutocloseTime,
      getFormattedCancelTime,
      showDealDetails,
      confirmDeal,
      refuseDeal,
      acceptDeal,
      confirmPayoutDeal,
      hasPayOutDealSla,
      showPayOutDealSla,
      isExpired,
      dealDetailsPopup,
      payOutDealSlaPopup,
      confirmPayoutPopup,
      confirmPopup,
      cancelReasonPopup,
      isVideoRequested,
      requestVideo,
      loader,
      load,
      update,
      store,
      dateHelper,
      DisputeReason,
      isPayOutReceiptCheck,
      api,
      DealStatus,
      numberFormatter,
      helper,
      PaymentOption,
      Direction,
      isEffectiveRateEnabled,
      rateHeader,
      isTrader: computed(() => store.getters.isTrader),
      isTraderOperator: computed(() => store.getters.isTraderOperator),
    }
  }
})
</script>
